import { BottomAction } from '@euler/app/components/BottomAction';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { TaskTimeoutInfo } from '@euler/app/flows/task/types';
import type { AppNavParams } from '@euler/app/Routes';
import { useAppLoading } from '@euler/components/loading/Provider';
import { kDefaultRolesAllowedToFinishOrder } from '@euler/functions/constants';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useAppReviewTracking } from '@euler/functions/useAppReviewTracking';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import {
  CommonTaskStatus,
  InspectionTaskJobStatus,
  InspectionTaskStatus,
  SamplingCheckResultType,
} from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { safeMarkDevFlag, sleep } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import isBefore from 'date-fns/isBefore';
import { memo, useCallback } from 'react';
import { InteractionManager } from 'react-native';
import { SharedValue } from 'react-native-reanimated';

const kTestFinishFlow = safeMarkDevFlag(false);

export const WiredTaskDetailFinishAction = memo(
  ({ scrollY }: { scrollY: SharedValue<number> }) => {
    const appLoading = useAppLoading();
    const trackAppReview = useAppReviewTracking();
    const viewReport = useViewReportWithTaskContext();
    const alert = useAlert();
    const confirm = useConfirm();
    const navigation =
      useNavigation<StackNavigationProp<TaskNavParams & AppNavParams>>();

    const role = useCurrentUserRole();

    const { taskService } = useServiceFactory();

    const {
      detail,
      taskManager,
      profile: { store },
      storeConfiguration,
    } = useTaskContext();

    const isChildTask = !detail.basicInfo.isMainOrder;

    const finishConstruction = useCallback(
      async (options?: { onSuccess: () => Promise<void> }) => {
        await appLoading.showWithAction(async () => {
          await taskManager.constructionManager.finish();
          if (options?.onSuccess != null) {
            await options.onSuccess();
          }
          trackAppReview();
        });
      },
      [appLoading, taskManager.constructionManager, trackAppReview],
    );

    const showAlert = (message: string, title?: string) => {
      void alert({
        title: title ?? '提示',
        message,
      });
    };

    const ensureInspectionsFinished = (): boolean => {
      if (
        detail.preInspection.status === CommonTaskStatus.InProgress ||
        detail.inspection.status === CommonTaskStatus.InProgress
      ) {
        if (detail.construction.status === CommonTaskStatus.InProgress) {
          showAlert('请先完成检测和施工项目并推送报告，然后进行完工交车!');
          return false;
        }
        if (
          detail.preInspection.status === CommonTaskStatus.InProgress &&
          detail.inspection.status === CommonTaskStatus.InProgress
        ) {
          showAlert('请先完成预检和全车检测项目并推送报告，然后进行完工交车!');
          return false;
        }

        if (detail.preInspection.status === CommonTaskStatus.InProgress) {
          showAlert('请先完成预检项目并推送报告，然后进行完工交车!');
          return false;
        }

        showAlert('请先完成全车检测项目并推送报告，然后进行完工交车!');
        return false;
      }

      return true;
    };

    const ensureDeliveryChecks = async (): Promise<boolean> => {
      if (detail.deliveryCheck.status === CommonTaskStatus.Pending) {
        if (
          detail.basicInfo.isMainOrder &&
          storeConfiguration.isDeliveryCheckOptional
        ) {
          return await confirm({
            title: '尚未进行竣工检验',
            message: '交车后车主将无质检报告可查看，是否确认继续完工交车? ',
          });
        }

        if (
          !detail.basicInfo.isMainOrder &&
          storeConfiguration.isChildOrderDeliveryCheckOptional
        ) {
          return await confirm({
            title: '尚未进行竣工检验',
            message: '是否继续完成子任务? ',
          });
        }

        void alert({ title: '请先进行竣工检验!' });
        return false;
      }

      if (detail.deliveryCheck.status === CommonTaskStatus.InProgress) {
        void alert({ title: '请先完成竣工检验!' });
        return false;
      }

      if (detail.samplingCheck.status === CommonTaskStatus.Pending) {
        if (
          detail.basicInfo.isMainOrder &&
          storeConfiguration.requireSamplingCheckBeforeFinish
        ) {
          void alert({ title: '请先进行完工抽检!' });
          return false;
        }

        if (
          !detail.basicInfo.isMainOrder &&
          storeConfiguration.requireSamplingCheckForChildOrderBeforeFinish
        ) {
          void alert({ title: '完成子任务前，需先进行抽检!' });
          return false;
        }
      }

      if (detail.samplingCheck.status === CommonTaskStatus.InProgress) {
        void alert({ title: '请先完成完工抽检!' });
        return false;
      }

      if (
        detail.samplingCheck.status === CommonTaskStatus.Finished &&
        detail.samplingCheck.resultType !== SamplingCheckResultType.Passed
      ) {
        if (detail.basicInfo.isMainOrder) {
          showAlert('完工抽检未通过，无法完工交车!');
        } else {
          showAlert('完工抽检未通过，无法完成当前子任务!');
        }

        return false;
      }

      return true;
    };

    const ensureConstructionFinished = (alertParams: {
      secondaryTitle?: string;
      onFinish: () => any;
    }) => {
      if (detail.construction.status === CommonTaskStatus.InProgress) {
        if (
          detail.construction.jobs.every(
            x => x.status === InspectionTaskJobStatus.Finished,
          )
        ) {
          showConstructionAlert(alertParams);
        } else {
          showAlert('请先完成施工项目并推送报告，然后进行完工交车!');
        }

        return false;
      }

      return true;
    };

    const showConstructionAlert = ({
      secondaryTitle,
      onFinish,
    }: {
      secondaryTitle?: string;
      onFinish: () => any;
    }) => {
      void alert({
        title: '完工交车提示',
        message: '尚未完成并推送施工报告，是否立即推送报告?',
        buttons: [
          {
            text: '取消',
            style: 'cancel',
          },
          {
            text: '预览施工报告',
            style: 'default',
            onPress: () => {
              AnalyticEvents.default.taskDeliveryCheckAlertPreviewButtonPressed();
              viewReport('constructionReport', {
                onFinish: async () => {
                  await finishConstruction();
                },
                async onFinishSuccess() {
                  navigation.goBack();
                  await sleep(1000);
                  await onFinish();
                },
              });
            },
          },
          {
            text: secondaryTitle ?? '直接推送并继续',
            style: 'destructive',
            onPress: async () => {
              AnalyticEvents.default.taskDeliveryCheckAlertFinishButtonPressed();
              await finishConstruction({
                onSuccess: async () => {
                  await onFinish();
                },
              });
            },
          },
        ],
      });
    };

    const prepareAndFinish = async (timeoutInfo?: TaskTimeoutInfo) => {
      if (isChildTask) {
        //#region finish task only.
        if (
          store.isIndependentDeliveryCheckEnabled ||
          storeConfiguration.isChildOrderDeliveryCheckOptional
        ) {
          if (
            !(await confirm({
              title: '确认完成当前子任务?',
            }))
          ) {
            return;
          }

          await appLoading.showWithAction(async () => {
            await taskManager.finish({
              timeoutInfo: timeoutInfo?.reason
                ? {
                    timeoutReason: timeoutInfo?.reason,
                    timeoutRemarks: timeoutInfo?.remark,
                  }
                : undefined,
            });
            navigation.goBack();
          });
        } else {
          await appLoading.showWithAction(async () => {
            await taskManager.deliveryCheckManager.prepare();
            navigation.push('_deliveryCheck', {
              timeoutInfo,
            });
          });
        }
      } else if (store.isIndependentDeliveryCheckEnabled) {
        await appLoading.showWithAction(async () => {
          const preparedData = await taskManager.getFinishPreparedData();
          await sleep(1000);
          navigation.push('_finish', {
            timeoutInfo,
            preparedData,
          });
        });
      } else {
        await appLoading.showWithAction(async () => {
          await taskManager.deliveryCheckManager.prepare();
          navigation.push('_deliveryCheck', {
            timeoutInfo,
          });
        });
      }
    };

    const finishWithDeliveryCheck = usePersistFn(() => {
      if (storeConfiguration.restrictedRolesForPreDeliveryCheck?.length) {
        const roleHelper = new OrgUserRoleHelper(role);
        if (
          !roleHelper.matchesAny(
            storeConfiguration.restrictedRolesForPreDeliveryCheck,
          )
        ) {
          void alert({ title: '对不起，您当前没有权限进行竣工检验!' });
          return;
        }
      }

      // legacy finish after delivery check w/o finishing delivery check first
      if (!ensureInspectionsFinished()) {
        return;
      }

      const onFinish = async () => {
        if (
          storeConfiguration.checkOrderTimeoutBeforeFinish &&
          detail.basicInfo.estimatedFinishTime != null &&
          isBefore(
            dateFromValue(detail.basicInfo.estimatedFinishTime),
            new Date(),
          )
        ) {
          navigation.push('_timeoutInfo', {
            estimatedFinishTime: detail.basicInfo.estimatedFinishTime,
            onContinue: async (reason, remarks) => {
              await prepareAndFinish({ reason, remark: remarks });
            },
          });
          return;
        } else {
          void prepareAndFinish();
        }
      };

      if (
        !ensureConstructionFinished({
          onFinish,
        })
      ) {
        return;
      }

      void InteractionManager.runAfterInteractions(async () => {
        await appLoading.showWithAction(onFinish);
      });
    });

    const finishIndependently = usePersistFn(async () => {
      const helper = new OrgUserRoleHelper(role);
      const hasPermission = storeConfiguration.restrictedRolesForFinishOrder
        ?.length
        ? helper.hasAnyRole(...storeConfiguration.restrictedRolesForFinishOrder)
        : helper.hasAnyRole(...kDefaultRolesAllowedToFinishOrder);

      if (!hasPermission) {
        showAlert(
          isChildTask
            ? '对不起，您没有权限进行该操作!'
            : '对不起，您没有权限进行完工交车!',
        );
        return;
      }

      const onFinish = async () => {
        if (
          storeConfiguration.checkOrderTimeoutBeforeFinish &&
          detail.basicInfo.estimatedFinishTime != null &&
          isBefore(
            dateFromValue(detail.basicInfo.estimatedFinishTime),
            new Date(),
          )
        ) {
          navigation.push('_timeoutInfo', {
            estimatedFinishTime: detail.basicInfo.estimatedFinishTime,
            onContinue: async (reason, remarks) => {
              await prepareAndFinish({ reason, remark: remarks });
            },
          });
          return;
        } else {
          void prepareAndFinish();
        }
      };

      if (detail.basicInfo.isMainOrder && detail.basicInfo.hasChildOrders) {
        if (!(await ensureDeliveryChecks())) {
          return;
        }
      } else {
        // finish independently after delivery check are finished
        if (!ensureInspectionsFinished()) {
          return;
        }

        if (
          !ensureConstructionFinished({
            onFinish: async () => {
              if (!(await ensureDeliveryChecks())) {
                return;
              }

              void InteractionManager.runAfterInteractions(onFinish);
            },
          })
        ) {
          return;
        }

        if (!(await ensureDeliveryChecks())) {
          return;
        }
      }

      void InteractionManager.runAfterInteractions(onFinish);
    });

    const onFinishPress = usePersistFn(async () => {
      if (kTestFinishFlow) {
        navigation.push('_timeoutInfo', {
          estimatedFinishTime:
            detail.basicInfo.estimatedFinishTime ?? '2024-10-10 14:00:00',
          onContinue: async (reason, remarks) => {
            await prepareAndFinish({ reason, remark: remarks });
          },
        });
        return;
      }

      if (detail.basicInfo.isMainOrder && detail.basicInfo.hasChildOrders) {
        const childTasks = await taskService.list(
          {
            parentTaskNo: detail.basicInfo.taskNo,
          },
          0,
          0,
          'createdAt',
          'asc',
        );

        for (const childTask of childTasks) {
          if (childTask.status === InspectionTaskStatus.Cancelled) {
            continue;
          }
          if (childTask.status !== InspectionTaskStatus.Finished) {
            showAlert('请先完成所有子任务!');
            return;
          }
        }
      }

      if (store.isIndependentDeliveryCheckEnabled) {
        void finishIndependently();
      } else {
        finishWithDeliveryCheck();
      }
    });

    if (
      detail.basicInfo.status === InspectionTaskStatus.Finished ||
      detail.basicInfo.status === InspectionTaskStatus.Cancelled
    ) {
      return null;
    }

    return (
      <BottomAction scrollY={scrollY}>
        <BottomAction.Button
          text={detail.basicInfo.isMainOrder ? '完工交车' : '完成子任务'}
          variant="primary"
          onPress={onFinishPress}
          style={
            !detail.basicInfo.isMainOrder
              ? {
                  backgroundColor: '#009FD4',
                }
              : undefined
          }
        />
      </BottomAction>
    );
  },
);
